import {Table} from 'notebook/tables/constants/TableConstants';
import {CellIndex, MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {BaseCellErrors} from 'notebook/tables/errors/TableErrorConstants';
import type {ASTNode} from 'notebook/tables/excess/AST';
import type {CellError, MissingSelectorType} from 'notebook/tables/excess/Util';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

import {QueryResolution} from '../TableStoreTransforms';
/**
 * Address of a cell in a table.
 *
 * If table and/or document names are not provided then the table and document are assumed to be the current
 * table and document.
 *
 * Row: row index in table
 * Column: column index in table
 * Table: Optional name of table
 * Document: Optional name of document
 */
export interface RelativeCellAddress extends CellIndex {
  table?: string | null;
  document?: string | null;
}

/**
 * Address of a cell in a table with resolved table name and document id.
 */
export interface ResolvedCellAddress extends CellIndex {
  tableName: string;
  documentId: string;
}

/**
 * The table and document names that describe where a cell is located.
 * This is used for arguments to functions that require table and document name to be specifed.
 */
export interface TableLocationNameArgs {
  tableName: string;
  documentName: string | null;
}

// ID string to use to perform single document evaluation with the multi-doc evaluator
// Used for compatibility with document id indexed maps and document addressed cell hashing
export const PLACEHOLDER_DOC_ID = 'PLACEHOLDER_DOC_ID';

export type CellNode = CellError | ASTNode;

export type EvaluatedCellError = BaseCellErrors;

export interface Selector {
  name: string;
  staticId: string;
  options: Array<string>;
}

export interface EvaluatedCell {
  value: TypedValue;
  error?: EvaluatedCellError;
  locked?: boolean;
  resolutions?: Array<QueryResolution>;
  selector?: Selector | MissingSelectorType | null;
  mergedBox?: MultiSelectionBox;
}

export interface MultidocFullTableMap {
  [docId: string]: FullTableMap;
}

export interface MultidocOutputTableMap {
  [docId: string]: OutputTableMap;
}

export interface FullTableMap {
  [tableName: string]: Table;
}

/**
 * A nested set of arrays containing the evaluated cells. The outer array indexes on rows, and the inner
 * array indexes on columns
 *
 * e.g.: EvaluatedTable[rowIndex][columnIndex]
 */
export type EvaluatedTable = Array<Array<EvaluatedCell>>;

export interface OutputTableMap {
  [tableName: string]: EvaluatedTable;
}

/**
 * Tracks metrics about latency and number of cycles until quiescence for evaluation.
 */
export interface EvaluationMetrics {
  startTime: DOMHighResTimeStamp;
  cycles: number;
}
